import { useState } from 'react';
import { ChevronLeft, ChevronRight, Shuffle, ExternalLink, BookOpen, FileText } from 'lucide-react';
import type { CognitiveBias } from '../../types';
import { CategoryFilter } from '../../components/practice/CategoryFilter';
import { Helmet } from 'react-helmet-async';
import { GradientTitle } from '../../components/ui';
import { MarkdownContent } from '../../components/ui/MarkdownContent';

interface FlipcardsPageProps {
  biases: CognitiveBias[];
}

export const FlipcardsPage: React.FC<FlipcardsPageProps> = ({ biases }) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [flipFilter, setFlipFilter] = useState('All');
  const [cardSource, setCardSource] = useState<'definition' | 'example'>('definition');

  // Get filtered cards
  const filteredCards = flipFilter === 'All'
    ? biases
    : biases.filter(b => b.category.toLowerCase() === flipFilter.toLowerCase());

  const flipCards = filteredCards.map(b => {
    // For definition-based cards
    if (cardSource === 'definition') {
      return {
        front: b.name,
        back: b.short_description
      };
    } 
    // For example-based cards
    else {
      // If the bias has examples, use the first one
      // Otherwise, fall back to the definition
      const hasExamples = b.examples && b.examples.length > 0;
      return {
        front: b.name,
        back: hasExamples 
          ? `${b.examples?.[0]?.description || ''}`
          : `No examples available. ${b.short_description}`
      };
    }
  });

  // Add this handler to reset the card index when filter changes
  const handleFilterChange = (newFilter: string) => {
    setIsFlipped(false);
    setCurrentCardIndex(0);
    setFlipFilter(newFilter);
  };

  // Handle source change
  const handleSourceChange = (source: 'definition' | 'example') => {
    setIsFlipped(false);
    setCardSource(source);
  };

  const shuffleCards = () => {
    // If card is flipped, turn it first then shuffle
    if (isFlipped) {
      setIsFlipped(false);
      // Wait for flip animation to complete before shuffling
      setTimeout(() => {
        const card = document.querySelector('.flip-card');
        card?.classList.add('shuffle-animation');
        
        setTimeout(() => {
          // Get a new random index that's different from the current one
          let randomIndex;
          do {
            randomIndex = Math.floor(Math.random() * filteredCards.length);
          } while (randomIndex === currentCardIndex && filteredCards.length > 1);
          
          setCurrentCardIndex(randomIndex);
          card?.classList.remove('shuffle-animation');
        }, 500);
      }, 600); // Wait for flip animation (0.6s) to complete
    } else {
      // If card is not flipped, just shuffle
      const card = document.querySelector('.flip-card');
      card?.classList.add('shuffle-animation');
      
      setTimeout(() => {
        // Get a new random index that's different from the current one
        let randomIndex;
        do {
          randomIndex = Math.floor(Math.random() * filteredCards.length);
        } while (randomIndex === currentCardIndex && filteredCards.length > 1);
        
        setCurrentCardIndex(randomIndex);
        card?.classList.remove('shuffle-animation');
      }, 500);
    }
  };

  const categories = new Set(biases.map(b => b.category));

  return (
    <div className="container mx-auto px-6 py-12">
      <Helmet prioritizeSeoTags>
        <title>Cognitive Bias Flashcards | Interactive Learning Tool</title>
        <meta name="description" content="Master cognitive biases with our interactive flashcards. Compatible with Anki and Quizlet for enhanced learning. Improve your understanding of how different biases affect decision-making." />
        <meta name="keywords" content="cognitive bias flashcards, Anki flashcards, Quizlet flashcards, memory training, psychology flashcards, decision-making biases, educational flashcards" />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/practice/flipcards/" key="canonical" data-source="page-component" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Cognitive Bias Flashcards | Interactive Learning Tool" />
        <meta property="og:description" content="Master cognitive biases with our interactive flashcards. Compatible with Anki and Quizlet for enhanced learning. Improve your understanding of how different biases affect decision-making." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/practice/flipcards/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Cognitive Bias Flashcards | Interactive Learning Tool" />
        <meta name="twitter:description" content="Master cognitive biases with our interactive flashcards. Test your knowledge and improve your understanding of how different biases affect decision-making." />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>
      
      {/* Main heading (H1) */}
      <div className="text-center mb-12">
        <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
          Cognitive Bias Flashcards
        </GradientTitle>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Test and improve your knowledge of cognitive biases with our interactive flashcards. 
          Select a category or shuffle through all biases to enhance your understanding.
        </p>
      </div>
      
      {/* Filter, Source and Shuffle Controls */}
      <div className="flex flex-col items-center gap-6 mb-8">
        {/* Category filter - ensure it's full width on mobile to allow proper scrolling */}
        <div className="w-full">
          <CategoryFilter
            categories={Array.from(categories)}
            activeFilter={flipFilter}
            onFilterChange={handleFilterChange}
          />
        </div>
        
        {/* Source selector and shuffle buttons */}
        <div className="flex flex-wrap justify-center gap-4">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            <button
              onClick={() => handleSourceChange('definition')}
              className={`flex items-center gap-2 px-5 py-3 transition-all duration-300 ${
                cardSource === 'definition' 
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BookOpen size={18} />
              Definitions
            </button>
            <button
              onClick={() => handleSourceChange('example')}
              className={`flex items-center gap-2 px-5 py-3 transition-all duration-300 ${
                cardSource === 'example' 
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FileText size={18} />
              Examples
            </button>
          </div>
          
          <button
            onClick={shuffleCards}
            className="group bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-3 rounded-xl text-white 
              hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Shuffle className="group-hover:rotate-180 transition-transform duration-300" />
              Shuffle
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        </div>
      </div>

      {/* Flip Card Section */}
      <div className="relative max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-2 md:gap-6">
          <button
            onClick={() => {
              setCurrentCardIndex((prev) => (prev - 1 + flipCards.length) % flipCards.length);
              setIsFlipped(false);
            }}
            className="p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-lg hover:scale-110 transition-transform duration-300"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>

          <div className="w-full md:w-[42rem] h-96">
            <div 
              onClick={() => setIsFlipped(!isFlipped)}
              className="flip-card w-full h-full cursor-pointer"
              style={{ perspective: "1000px" }}
            >
              <div
                style={{
                  transformStyle: "preserve-3d",
                  transform: isFlipped ? "rotateY(180deg)" : "",
                  transition: "transform 0.6s"
                }}
                className="relative w-full h-full"
              >
                {/* Front */}
                <div
                  className="absolute w-full h-full bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg p-4 sm:p-8 border border-gray-100"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <div className="flex items-center justify-center h-full">
                    <p className="text-xl sm:text-2xl font-semibold text-gray-800 text-center">
                      {flipCards[currentCardIndex].front}
                    </p>
                  </div>
                </div>

                {/* Back */}
                <div
                  className="absolute w-full h-full bg-gradient-to-br from-orange-50 to-pink-50 rounded-3xl shadow-lg p-4 sm:p-8 overflow-auto"
                  style={{
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)"
                  }}
                >
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-gray-800 w-full">
                      <MarkdownContent 
                        content={flipCards[currentCardIndex].back} 
                        className="text-center text-sm sm:text-base"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              setCurrentCardIndex((prev) => (prev + 1) % flipCards.length);
              setIsFlipped(false);
            }}
            className="p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-lg hover:scale-110 transition-transform duration-300"
          >
            <ChevronRight size={20} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Explanation and Links Section */}
      <div className="mt-16 max-w-5xl mx-auto">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-5 mb-6">
            <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 flex items-center justify-center flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><path d="M12 17h.01"></path></svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">About Cognitive Bias Flashcards</h2>
          </div>
          
          <div className="text-gray-700 mb-8">
            Flashcards are an effective way to learn cognitive biases. The simple format helps you memorize each bias and its definition, building a mental library of these thinking patterns.
          </div>
          
          <div className="text-gray-700 mb-8">
            Just 5-10 minutes of flashcard practice daily can dramatically improve your ability to spot biases in real-world situations and make more objective decisions.
          </div>

          <div className="text-gray-700 mb-10">
            <span className="font-medium">Study Mode:</span> Use the toggle above to switch between definitions and real-world examples. Definitions help you understand the concept, while examples show you how biases manifest in everyday situations.
          </div>

          <div className="mb-4 text-xl font-medium text-gray-800">Continue Your Learning Journey:</div>
          
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="text-orange-500 text-2xl flex-shrink-0 mt-0.5">●</div>
              <div>
                <a 
                  href="https://quizlet.com/ie/1030426566/cognitive-biases-flash-cards/?i=2w31ng&x=1jqt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1"
                >
                  Quizlet Flashcards <ExternalLink size={16} />
                </a>
                <div className="text-gray-700 mt-1">Take these concepts on the go with our Quizlet deck – perfect for quick study sessions during your commute or coffee break</div>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="text-orange-500 text-2xl flex-shrink-0 mt-0.5">●</div>
              <div>
                <a 
                  href="https://ankiweb.net/shared/info/386854006"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:underline font-medium inline-flex items-center gap-1"
                >
                  Anki Flashcards <ExternalLink size={16} />
                </a>
                <div className="text-gray-700 mt-1">For dedicated learners, our Anki deck uses smart repetition to help these concepts stick in your long-term memory</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 